import { DownOutlined, LoadingOutlined } from '@ant-design/icons';
import { Button, Input, Popover, Select, Spin } from 'antd';
import cn from 'classnames';
import { useCallback, useMemo, useState } from 'react';

import { type LeadCardResponsibleEmployeeData } from '@app/components/LeadCard/data';

import type { ChangeEvent, FC, MouseEvent } from 'react';

import './ResponsibleSelect.scss';

interface ResponsibleSelectProps {
  employees: LeadCardResponsibleEmployeeData[];
  selectedEmployeeId: LeadCardResponsibleEmployeeData['id'] | null;
  isLoading?: boolean;
  isDisabled?: boolean;
  mode?: 'panel' | 'cell';
  placeholder?: string;
  showTitle?: boolean;
  onSelect(
    employeeId: LeadCardResponsibleEmployeeData['id']
  ): void | Promise<void>;
}

const RESPONSIBLE_OPTION_ROW_HEIGHT = 40;
const RESPONSIBLE_OPTION_LIST_HEIGHT = 320;
const RESPONSIBLE_OPTION_OVERSCAN = 6;

interface ResponsibleVirtualListProps {
  employees: LeadCardResponsibleEmployeeData[];
  selectedEmployeeId: LeadCardResponsibleEmployeeData['id'] | null;
  onSelect(employeeId: LeadCardResponsibleEmployeeData['id']): void;
}

const getEmployeeKey = ({ id }: LeadCardResponsibleEmployeeData) => String(id);

const ResponsibleVirtualList: FC<ResponsibleVirtualListProps> = ({
  employees,
  selectedEmployeeId,
  onSelect,
}) => {
  const [scrollTop, setScrollTop] = useState(0);

  const totalHeight = employees.length * RESPONSIBLE_OPTION_ROW_HEIGHT;
  const visibleRowsCount = Math.ceil(
    RESPONSIBLE_OPTION_LIST_HEIGHT / RESPONSIBLE_OPTION_ROW_HEIGHT
  );
  const maxStartIndex = Math.max(0, employees.length - visibleRowsCount);
  const startIndex = Math.min(
    maxStartIndex,
    Math.max(
      0,
      Math.floor(scrollTop / RESPONSIBLE_OPTION_ROW_HEIGHT) -
        RESPONSIBLE_OPTION_OVERSCAN
    )
  );
  const endIndex = Math.min(
    employees.length,
    startIndex + visibleRowsCount + RESPONSIBLE_OPTION_OVERSCAN * 2
  );
  const visibleEmployees = employees.slice(startIndex, endIndex);
  const offsetTop = startIndex * RESPONSIBLE_OPTION_ROW_HEIGHT;

  if (employees.length === 0) {
    return <div className="ResponsibleSelect__empty">Нет результатов</div>;
  }

  return (
    <div
      className="ResponsibleSelect__virtual-list"
      style={{ height: Math.min(RESPONSIBLE_OPTION_LIST_HEIGHT, totalHeight) }}
      onScroll={({ currentTarget: { scrollTop } }) => setScrollTop(scrollTop)}
    >
      <div
        className="ResponsibleSelect__virtual-spacer"
        style={{ height: totalHeight }}
      >
        <div
          className="ResponsibleSelect__virtual-items"
          style={{ transform: `translateY(${offsetTop}px)` }}
        >
          {visibleEmployees.map((employee) => {
            const isSelected = employee.id === selectedEmployeeId;

            return (
              <button
                className={cn('ResponsibleSelect__option-button', {
                  'ResponsibleSelect__option-button--selected': isSelected,
                })}
                key={getEmployeeKey(employee)}
                type="button"
                onClick={() => onSelect(employee.id)}
              >
                <span className="ResponsibleSelect__option ResponsibleSelect__option--virtual">
                  {employee.name}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const ResponsibleSelect: FC<ResponsibleSelectProps> = ({
  employees,
  selectedEmployeeId,
  isLoading = false,
  isDisabled = false,
  mode = 'panel',
  placeholder = 'Выберите ответственного',
  showTitle = true,
  onSelect,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const options = useMemo(
    () =>
      employees.map(({ id, name }) => ({
        value: id,
        label: name,
      })),
    [employees]
  );
  const selectedValue = selectedEmployeeId ?? undefined;
  const selectedEmployeeName = useMemo(
    () =>
      employees.find(({ id }) => id === selectedEmployeeId)?.name ??
      options.find(({ value }) => value === selectedEmployeeId)?.label ??
      null,
    [employees, options, selectedEmployeeId]
  );
  const filteredEmployees = useMemo(() => {
    const query = searchValue.trim().toLowerCase();

    if (!query) return employees;

    return employees.filter(({ id, name }) => {
      const idText = String(id).toLowerCase();
      const nameText = name.toLowerCase();

      return idText.includes(query) || nameText.includes(query);
    });
  }, [employees, searchValue]);

  const clickHandler = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  }, []);

  const openChangeHandler = useCallback(
    (nextOpen: boolean) => {
      setIsOpen(nextOpen && !isDisabled && !isLoading);

      if (!nextOpen) {
        setSearchValue('');
      }
    },
    [isDisabled, isLoading]
  );

  const cellSelectHandler = useCallback(
    (employeeId: LeadCardResponsibleEmployeeData['id']) => {
      setIsOpen(false);
      setSearchValue('');
      onSelect(employeeId);
    },
    [onSelect]
  );

  const searchChangeHandler = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      setSearchValue(value);
    },
    []
  );

  if (mode === 'cell') {
    const isUnavailable = isDisabled || isLoading;

    return (
      <section
        className={`ResponsibleSelect ResponsibleSelect--${mode}`}
        onClick={clickHandler}
      >
        <Popover
          content={
            <div
              className="ResponsibleSelect__custom-dropdown"
              onClick={clickHandler}
            >
              <Input
                className="ResponsibleSelect__search"
                value={searchValue}
                placeholder="Поиск"
                disabled={isUnavailable}
                onChange={searchChangeHandler}
              />
              {isLoading ? (
                <div className="ResponsibleSelect__loading">
                  <Spin size="small" />
                </div>
              ) : (
                <ResponsibleVirtualList
                  key={searchValue}
                  employees={filteredEmployees}
                  selectedEmployeeId={selectedEmployeeId}
                  onSelect={cellSelectHandler}
                />
              )}
            </div>
          }
          trigger="click"
          open={isOpen && !isUnavailable}
          placement="bottomLeft"
          arrow={false}
          onOpenChange={openChangeHandler}
          rootClassName="ResponsibleSelect__custom-popover"
        >
          <Button
            className={cn('ResponsibleSelect__cell-trigger', {
              'ResponsibleSelect__cell-trigger--open': isOpen,
            })}
            disabled={isUnavailable}
            aria-label="Ответственный"
            aria-expanded={isOpen && !isUnavailable}
          >
            <span
              className={cn('ResponsibleSelect__cell-value', {
                'ResponsibleSelect__cell-value--placeholder':
                  !selectedEmployeeName,
              })}
            >
              {selectedEmployeeName ?? placeholder}
            </span>
            {isLoading ? (
              <LoadingOutlined className="ResponsibleSelect__cell-icon" />
            ) : (
              <DownOutlined
                className={cn('ResponsibleSelect__cell-icon', {
                  'ResponsibleSelect__cell-icon--open': isOpen,
                })}
              />
            )}
          </Button>
        </Popover>
      </section>
    );
  }

  return (
    <section
      className={`ResponsibleSelect ResponsibleSelect--${mode}`}
      onClick={clickHandler}
    >
      {showTitle && <h4 className="ResponsibleSelect__title">Ответственный</h4>}
      <Select<LeadCardResponsibleEmployeeData['id']>
        className="ResponsibleSelect__control"
        value={selectedValue}
        options={options}
        optionRender={(option) => (
          <span className="ResponsibleSelect__option">{option.label}</span>
        )}
        placeholder={placeholder}
        size="small"
        loading={isLoading}
        disabled={isDisabled || isLoading}
        onChange={onSelect}
        showSearch
        optionFilterProp="label"
        optionLabelProp="label"
        popupMatchSelectWidth={false}
        aria-label="Ответственный"
        classNames={{
          popup: {
            root: `ResponsibleSelect__dropdown ResponsibleSelect__dropdown--${mode}`,
          },
        }}
      />
    </section>
  );
};
